import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';

import EditBtn from '../../Button/EditBtn';
import Button from '../../Button';
import UnitField from './UnitField';
import { COMMON } from '../../../constants/strings';
import { enhance } from './enhance';
import {
  getHeightOptions,
  getWeightOptions,
  getTemperatureOptions,
  getGlucoseOptions,
} from './constants';

import styles from './index.styl';

const UnitsSettings = ({
  intl,
  currentMetricHeightType,
  currentMetricWeightType,
  currentMetricGlucoseType,
  currentMetricTemperatureType,
  userId,
  setCurrentMetric,
  editUser,
  profile,
  profileTargetUser,
}) => {
  const { settings, id } = isEmpty(profileTargetUser)
    ? profile
    : profileTargetUser;

  const { height, weight, temperature, glucose } = get(
    settings,
    'measurementUnitsSettings',
    {},
  );

  const [editMode, setEditMode] = useState(false);
  const { formatMessage } = intl;

  const glucoseOptions = useMemo(() => getGlucoseOptions(formatMessage), []);
  const heightOptions = useMemo(() => getHeightOptions(formatMessage), []);
  const weightOptions = useMemo(() => getWeightOptions(formatMessage), []);
  const temperatureOptions = useMemo(
    () => getTemperatureOptions(formatMessage),
    [],
  );

  useEffect(() => {
    handleSetMetric('currentMetricWeight', weight);
    handleSetMetric('currentMetricHeight', height);
    handleSetMetric('currentMetricTemperature', temperature);
    handleSetMetric('currentMetricGlucose', glucose);
  }, [id]);

  const handleSaveUnits = () => {
    const measurementUnitsSettings = {
      height: currentMetricHeightType,
      weight: currentMetricWeightType,
      glucose: currentMetricGlucoseType,
      temperature: currentMetricTemperatureType,
      distance: 'miles', // REQUIRED FIELD ON BE
    };
    editUser({ id: userId, measurementUnitsSettings });
    setEditMode(false);
  };

  const handleCancel = () => {
    handleSetMetric('currentMetricWeight', weight);
    handleSetMetric('currentMetricHeight', height);
    handleSetMetric('currentMetricTemperature', temperature);
    handleSetMetric('currentMetricGlucose', glucose);
    setEditMode(false);
  };

  const handleSetMetric = (type, value) => {
    setCurrentMetric({ type, value });
  };

  return (
    <section>
      <div className={styles.titleWrapper}>
        <h3 className={styles.title}>
          {formatMessage({ id: 'profile.section.unitsSettings.title' })}
        </h3>
        <EditBtn onClick={() => setEditMode(!editMode)} />
      </div>
      <div className={styles.unitsWrapper}>
        <UnitField
          title="profile.section.unitsSettings.height"
          editMode={editMode}
          value={currentMetricHeightType}
          viewValue={`profile.section.unitsSettings.height.metric.${currentMetricHeightType}`}
          options={heightOptions}
          onChange={({ value }) =>
            handleSetMetric('currentMetricHeight', value)
          }
        />

        <UnitField
          title="profile.section.unitsSettings.weight"
          editMode={editMode}
          value={currentMetricWeightType}
          viewValue={`profile.section.unitsSettings.weight.metric.${currentMetricWeightType}`}
          options={weightOptions}
          onChange={({ value }) =>
            handleSetMetric('currentMetricWeight', value)
          }
        />

        <UnitField
          title="profile.section.unitsSettings.temperature"
          editMode={editMode}
          value={currentMetricTemperatureType}
          viewValue={`profile.section.unitsSettings.temperature.metric.${currentMetricTemperatureType}`}
          options={temperatureOptions}
          onChange={({ value }) =>
            handleSetMetric('currentMetricTemperature', value)
          }
        />

        <UnitField
          title="profile.section.unitsSettings.glucose"
          editMode={editMode}
          value={currentMetricGlucoseType}
          viewValue={`profile.section.unitsSettings.glucose.metric.${currentMetricGlucoseType}`}
          options={glucoseOptions}
          onChange={({ value }) =>
            handleSetMetric('currentMetricGlucose', value)
          }
        />
      </div>
      <If condition={editMode}>
        <Button
          className="mt3 mr2 user-profile-save-button"
          raised
          color="primary"
          onClick={handleSaveUnits}
        >
          <FormattedMessage {...COMMON.save} />
        </Button>
        <Button
          className="mt3 user-profile-cancel-button"
          raised
          color="accent"
          onClick={handleCancel}
        >
          <FormattedMessage {...COMMON.cancel} />
        </Button>
      </If>
    </section>
  );
};

UnitsSettings.propTypes = {
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }),
  profile: PropTypes.object,
  userId: PropTypes.number.isRequired,
  profileTargetUser: PropTypes.object,
  editUser: PropTypes.func.isRequired,
  setCurrentMetric: PropTypes.func.isRequired,
  currentMetricHeightType: PropTypes.string.isRequired,
  currentMetricWeightType: PropTypes.string.isRequired,
  currentMetricGlucoseType: PropTypes.string.isRequired,
  currentMetricTemperatureType: PropTypes.string.isRequired,
};

export default enhance(UnitsSettings);



// WEBPACK FOOTER //
// ./src/components/ProfileLayout/UnitsSettings/index.jsx